import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { DialogEllipsisTooltipDirective } from '../../../shared/dialog-ellipsis-tooltip.directive';

export interface EditDialogData {
  openedCategoriaKey: string;
  editingSubcategoria: any;
}

@Component({
  selector: 'app-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    DialogEllipsisTooltipDirective,
  ],
  templateUrl: './edit-dialog.component.html',
  styleUrl: '../mantenedor.component.scss',
})
export class EditDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<EditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditDialogData,
  ) {}

  hasSortChanges: boolean = false;
  selectedFieldLabel: string | null = null;
  selectedField: any | null = null;
  selectedTriggerValue: string = '';
  selectedSubcampoKey: string = '';
  hasRelationChanges: boolean = false;
  deletedRelations: Array<{ field_name: string; properties: string[] }> = [];
  // Store original values to detect changes
  originalTriggerValue: string = '';
  originalSubcampoKey: string = '';
  // Cache for otherFields to avoid recalculating
  otherFields: any[] = [];

  get sortedFields(): any[] {
    const fields = (this.data?.editingSubcategoria?.fields ?? []) as any[];
    return [...fields].sort((a: any, b: any) => {
      const sa = a?.sort ?? 0;
      const sb = b?.sort ?? 0;
      return sa - sb;
    });
  }

  moveDown(index: number): void {
    const fields = this.data?.editingSubcategoria?.fields as any[];
    if (!Array.isArray(fields)) return;
    if (index < 0 || index >= fields.length - 1) return;
    // Swap positions and then reassign sequential sorts
    [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
    this.normalizeSorts(fields);
    this.hasSortChanges = true;
  }

  moveUp(index: number): void {
    const fields = this.data?.editingSubcategoria?.fields as any[];
    if (!Array.isArray(fields)) return;
    if (index <= 0 || index >= fields.length) return;
    // Swap positions and then reassign sequential sorts
    [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
    this.normalizeSorts(fields);
    this.hasSortChanges = true;
  }

  private normalizeSorts(fields: any[]): void {
    fields.forEach((f, i) => {
      if (f) f.sort = i + 1; // ensure 1-based sort values
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    const categoria = this.data?.openedCategoriaKey ?? '';
    const subcategoria = this.data?.editingSubcategoria?.key ?? '';

    // Prepare fields array for relation changes (new relations)
    let fieldsForRelation: any[] = [];
    let deletesToAdd: Array<{ field_name: string; properties: string[] }> = [];

    // If editing a relation for picklist and both values selected, prepare relation payload
    if (
      (this.selectedField?.field_type ?? '') === 'picklist' &&
      !!this.selectedFieldLabel &&
      !!this.selectedTriggerValue &&
      !!this.selectedSubcampoKey
    ) {
      const resolveFieldName = (labelOrName: string) => {
        const all = (this.data?.editingSubcategoria?.fields ?? []) as any[];
        const found = all.find(
          (f: any) =>
            (f?.label ?? '') === labelOrName ||
            (f?.field_name ?? '') === labelOrName,
        );
        return found?.field_name ?? found?.label ?? labelOrName;
      };
      const selectedFieldName = resolveFieldName(this.selectedFieldLabel);
      const subcampoFieldName = resolveFieldName(this.selectedSubcampoKey);

      // Check if there are changes in existing relations
      const hasOriginalRelation =
        !!this.originalTriggerValue || !!this.originalSubcampoKey;
      const hasChanges =
        this.originalTriggerValue !== this.selectedTriggerValue ||
        this.originalSubcampoKey !== this.selectedSubcampoKey;

      // If there were original values and they changed, add deletes
      if (hasOriginalRelation && hasChanges) {
        const deletedProps: string[] = [];
        if (this.originalTriggerValue) deletedProps.push('desencadenante');
        if (this.originalSubcampoKey) deletedProps.push('subcampo');

        if (deletedProps.length > 0) {
          deletesToAdd.push({
            field_name: selectedFieldName,
            properties: deletedProps,
          });
        }

        // Also delete campo_padre from the original subcampo if it exists
        if (this.originalSubcampoKey) {
          const originalSubcampoFieldName = resolveFieldName(
            this.originalSubcampoKey,
          );
          deletesToAdd.push({
            field_name: originalSubcampoFieldName,
            properties: ['campo_padre'],
          });
        }
      }

      fieldsForRelation = [
        {
          field_name: selectedFieldName,
          subcampo: subcampoFieldName,
          desencadenante: this.selectedTriggerValue,
        },
        {
          field_name: subcampoFieldName,
          campo_padre: selectedFieldName,
        },
      ];
    }

    // Combine deletesToAdd with existing deletedRelations
    const allDeletes = [...this.deletedRelations, ...deletesToAdd];
    const hasDeletes = this.hasRelationChanges || allDeletes.length > 0;

    // If we have both deletes and fields (relation changes), combine them
    if (hasDeletes && fieldsForRelation.length > 0) {
      const relationBody = [
        {
          categoria,
          subcategoria,
          deletes: allDeletes,
          fields: fieldsForRelation,
        },
      ];
      this.dialogRef.close({ fieldsRelationChanges: relationBody });
      return;
    }

    // If we only have fields (relation changes), send them
    if (fieldsForRelation.length > 0) {
      const relationBody = [
        {
          categoria,
          subcategoria,
          fields: fieldsForRelation,
        },
      ];
      this.dialogRef.close({ fieldsRelationChanges: relationBody });
      return;
    }

    // If we only have deletes, send them
    if (hasDeletes) {
      const relationBody = [
        {
          categoria,
          subcategoria,
          deletes: allDeletes,
        },
      ];
      this.dialogRef.close({ fieldsRelationChanges: relationBody });
      return;
    }

    // Otherwise, return sort payload based on current view order (sortedFields)
    const fieldsOrdered = this.sortedFields;
    const fieldsPayload = fieldsOrdered.map((f: any, index: number) => ({
      field_name: f?.field_name ?? f?.name ?? f?.label ?? '',
      sort: index + 1, // ensure 1-based and matches the displayed order
    }));
    const sortBody = [
      {
        categoria,
        subcategoria,
        fields: fieldsPayload,
      },
    ];
    this.dialogRef.close({ sortChanges: sortBody });
  }

  close(): void {
    this.dialogRef.close();
  }

  onClickEditField(field: any): void {
    this.selectedFieldLabel = field?.label ?? field?.field_name ?? '';
    this.selectedField = field || null;

    // Update otherFields when a field is selected
    this.updateOtherFields();

    // Si el campo ya tiene datos de relación, rellenar los selects y guardar valores originales
    if (field?.desencadenante) {
      this.selectedTriggerValue = field.desencadenante;
      this.originalTriggerValue = field.desencadenante;
    } else {
      this.selectedTriggerValue = '';
      this.originalTriggerValue = '';
    }

    if (field?.subcampo) {
      this.selectedSubcampoKey = field.subcampo;
      this.originalSubcampoKey = field.subcampo;
    } else {
      this.selectedSubcampoKey = '';
      this.originalSubcampoKey = '';
    }
  }

  onDeleteRelation(): void {
    if (!this.selectedField) return;

    const subcampoKey = this.selectedField.subcampo;
    const selectedFieldName =
      this.selectedField.field_name ?? this.selectedField.label ?? '';

    // Track deleted properties for the selected field
    const deletedProps: string[] = [];
    if (this.selectedField.desencadenante) deletedProps.push('desencadenante');
    if (this.selectedField.subcampo) deletedProps.push('subcampo');

    if (deletedProps.length > 0) {
      this.deletedRelations.push({
        field_name: selectedFieldName,
        properties: deletedProps,
      });
    }

    // Eliminar desencadenante y subcampo del campo seleccionado
    delete this.selectedField.desencadenante;
    delete this.selectedField.subcampo;

    // Buscar el subcampo y eliminar su campo_padre
    if (subcampoKey) {
      const allFields = (this.data?.editingSubcategoria?.fields ?? []) as any[];
      const subcampoField = allFields.find(
        (f: any) =>
          (f?.label ?? f?.field_name) === subcampoKey ||
          (f?.field_name ?? '') === subcampoKey,
      );
      if (subcampoField && subcampoField.campo_padre) {
        const subcampoFieldName =
          subcampoField.field_name ?? subcampoField.label ?? '';
        this.deletedRelations.push({
          field_name: subcampoFieldName,
          properties: ['campo_padre'],
        });
        delete subcampoField.campo_padre;
      }
    }

    // Resetear los valores del formulario
    this.selectedTriggerValue = '';
    this.selectedSubcampoKey = '';
    this.hasRelationChanges = true;
  }

  get isSelectedFieldPicklist(): boolean {
    return (this.selectedField?.field_type ?? '') === 'picklist';
  }

  get canSaveRelations(): boolean {
    return (
      this.isSelectedFieldPicklist &&
      !!this.selectedFieldLabel &&
      !!this.selectedTriggerValue &&
      !!this.selectedSubcampoKey
    );
  }

  get canSaveChanges(): boolean {
    return (
      this.hasSortChanges || this.canSaveRelations || this.hasRelationChanges
    );
  }

  private updateOtherFields(): void {
    const all = (this.data?.editingSubcategoria?.fields ?? []) as any[];
    if (!this.selectedField) {
      this.otherFields = all;
    } else {
      this.otherFields = all.filter(
        (f) =>
          (f?.label ?? f?.field_name) !== this.selectedFieldLabel &&
          !f.campo_padre,
      );
    }
  }
}
